<template>
    <App title="Station">
        <div class="station-layout">

            <!-- ── Hero ──────────────────────────────────────────────────── -->
            <div class="station-hero">
                <div class="station-avatar">
                    <i class="fas fa-charging-station"></i>
                </div>
                <div class="station-hero-info">
                    <h2 class="station-hero-name">{{ station.title }}</h2>
                    <span class="station-type-badge">{{ station.type || 'Station' }}</span>
                    <p class="station-hero-address">
                        <i class="fas fa-map-marker-alt me-1"></i>{{ station.address || '—' }}
                    </p>
                </div>
                <div class="station-hero-actions">
                    <a :href="route('station.page.update', station.slug)" class="btn btn-sm btn-outline-secondary">
                        <i class="fas fa-pencil-alt me-1"></i> Edit Station
                    </a>
                </div>
            </div>

            <!-- ── Details grid ───────────────────────────────────────────── -->
            <div class="station-grid">
                <div class="station-field">
                    <span class="station-field-icon"><i class="fas fa-store"></i></span>
                    <div>
                        <p class="station-field-label">Title</p>
                        <p class="station-field-value">{{ station.title || '—' }}</p>
                    </div>
                </div>

                <div class="station-field">
                    <span class="station-field-icon"><i class="fas fa-tag"></i></span>
                    <div>
                        <p class="station-field-label">Type</p>
                        <p class="station-field-value">{{ station.type || '—' }}</p>
                    </div>
                </div>

                <div class="station-field">
                    <span class="station-field-icon"><i class="fas fa-map-marker-alt"></i></span>
                    <div>
                        <p class="station-field-label">Address</p>
                        <p class="station-field-value">{{ station.address || '—' }}</p>
                    </div>
                </div>

                <div class="station-field">
                    <span class="station-field-icon"><i class="fas fa-hashtag"></i></span>
                    <div>
                        <p class="station-field-label">Station ID</p>
                        <p class="station-field-value">#{{ station.id }}</p>
                    </div>
                </div>
            </div>

            <!-- ── Account details ────────────────────────────────────────── -->
            <div v-if="station.account" class="station-section">
                <div class="station-section-header">
                    <i class="fas fa-university me-2 text-muted"></i>
                    <span>Bank Account</span>
                </div>
                <div class="station-section-body">
                    <div class="station-grid">
                        <div class="station-field">
                            <span class="station-field-icon"><i class="fas fa-university"></i></span>
                            <div>
                                <p class="station-field-label">Bank</p>
                                <p class="station-field-value">{{ station.account.bank || '—' }}</p>
                            </div>
                        </div>
                        <div class="station-field">
                            <span class="station-field-icon"><i class="fas fa-credit-card"></i></span>
                            <div>
                                <p class="station-field-label">Account Number</p>
                                <p class="station-field-value">{{ station.account.number || '—' }}</p>
                            </div>
                        </div>
                        <div class="station-field">
                            <span class="station-field-icon"><i class="fas fa-user-circle"></i></span>
                            <div>
                                <p class="station-field-label">Account Name</p>
                                <p class="station-field-value">{{ station.account.name || '—' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Tabbed Records Section ─────────────────────────────────── -->
            <div class="station-section">

                <!-- Tab Header -->
                <div class="station-tab-header">


                    <div class="tab-toggle-pill">
                        <button class="pill-option" :class="{ active: activeTab === 'claims' }" @click="switchTab('claims')">
                            <i class="fas fa-file-invoice"></i>
                            Claims
                        </button>
                        <button class="pill-option" :class="{ active: activeTab === 'inventory' }" @click="switchTab('inventory')">
                            <i class="fas fa-boxes"></i>
                            Inventory
                        </button>
                        <span class="pill-slider" :class="activeTab"></span>
                    </div>
                </div>

                <!-- Tab Panels -->
                <div class="station-tab-panels">
                    <transition name="tab-fade" mode="out-in">
                        <div key="claims" v-show="activeTab === 'claims'" class="station-section-body">
                            <Datatable :options="claimsOptions" />
                        </div>
                    </transition>
                    <transition name="tab-fade" mode="out-in">
                        <div key="inventory" v-show="activeTab === 'inventory'" class="station-section-body">
                            <Datatable :options="inventoryOptions" :scopes=scope />
                        </div>
                    </transition>
                </div>

            </div>

        </div>
    </App>
</template>

<script lang="ts">
import type Station from '@/models/Station'
import Block from '@/vue/components/Block.vue'
import Datatable from '@/vue/components/Datatable.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import App from '@/vue/templates/App.vue'
import { CellRender } from '@/vue/utils/Datatable'
import { Link } from '@inertiajs/vue3'
import { Component, Prop, toNative, Vue } from 'vue-facing-decorator'

@Component({
    components: {
        App,
        Block,
        TextBlock,
        Datatable,
        Link,
    },
})
class Single extends Vue {

    stockTypeCounts: Record<string, number> = {}
    @Prop({ type: Object })
    readonly station!: Station

    activeTab: 'claims' | 'inventory' = 'claims'
    claimsCount = 0
    inventoryCount = 0

    mounted() {
        this.loadStockTypes()
        this.loadStationStocks()
    }

    async loadStockTypes() {
        try {
            const { data } = await this.$http.get(this.route('stock.ajax.filters'))
            this.allStockTypes = data.types ?? []
        } catch (e) {
            console.error('Error loading stock types:', e)
        }
    }

    async loadStationStocks() {
        try {
            const { data } = await this.$http.get(
                this.route('station.ajax.stocks.type', { station: this.station })
            )
            this.stockTypeCounts = data ?? {}
        } catch (e) {
            console.error('Error loading stock types:', e)
        }
    }

    switchTab(tab: 'claims' | 'inventory') {
        this.activeTab = tab
    }

    get claimsColumns() {
        let columns: any[] = []

        columns.push({
            data: 'id',
            orderable: true,
            name: 'id',
            title: 'Id',
        })

        columns.push({
            data: 'code.value',
            orderable: false,
            title: 'Generated Code',
        })

        columns.push({
            data: 'amount',
            orderable: false,
            title: 'Amount',
            render: CellRender.money(),
        })

        columns.push({
            data: 'rider.fullname',
            title: 'Rider',
            orderable: false,
        })

        columns.push({
            data: 'code.order.engine.serial',
            title: 'Engine',
            orderable: false,
        })

        columns.push({
            data: 'created_at',
            title: 'Registered',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        return columns
    }

    allStockTypes: string[] = []
    stationStocks: any[] = []

    get scope(): { value: string; label: string }[] {
        return Object.entries(this.stockTypeCounts).map(([type, count]) => ({
            value: type,
            label: `${type} (${count})`,
        }))
    }

    get inventoryColumns() {
        let columns: any[] = []

        columns.push({
            data: 'id',
            orderable: true,
            name: 'id',
            title: 'Id',
        })

        columns.push({
            data: 'serial',
            orderable: true,
            name: 'serial',
            title: 'Serial Number',
        })

        columns.push({
            data: 'type',
            orderable: true,
            name: 'type',
            title: 'item Type',
        })



        columns.push({
            data: 'created_at',
            title: 'Added',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        return columns
    }

    get claimsOptions() {
        return {
            ajax: {
                url: this.route('station.ajax.table.claims', { station: this.station }),
                method: 'POST',
            },
            columns: this.claimsColumns,
        }
    }

    get inventoryOptions() {
        return {
            ajax: {
                url: this.route('station.ajax.table.stocks', { station: this.station }),
                method: 'GET',
            },
            columns: this.inventoryColumns,
        }
    }
}

export default toNative(Single)
</script>

<style scoped lang="scss">
.station-layout {
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

// ─── Hero ──────────────────────────────────────────────────────────────────
.station-hero {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    flex-wrap: wrap;
}

.station-avatar {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.station-hero-info {
    flex: 1;
    min-width: 0;
}

.station-hero-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.3rem;
    line-height: 1.2;
}

.station-type-badge {
    display: inline-block;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: #dcfce7;
    color: #15803d;
    margin-bottom: 0.4rem;
}

.station-hero-address {
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0.3rem 0 0;
}

.station-hero-actions {
    flex-shrink: 0;
    margin-left: auto;
}

// ─── Detail grid ───────────────────────────────────────────────────────────
.station-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.875rem;

    @media (min-width: 900px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.station-field {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
    background: #fff;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.15s, border-color 0.15s;

    &:hover {
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
        border-color: #d1fae5;
    }
}

.station-field-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #f0fdf4;
    color: #16a34a;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    margin-top: 0.1rem;
}

.station-field-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin-bottom: 0.2rem;
}

.station-field-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: #111827;
    margin: 0;
    word-break: break-word;
}

// ─── Section cards ─────────────────────────────────────────────────────────
.station-section {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    }
}

.station-section-header {
    display: flex;
    align-items: center;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(to right, #f9fafb, #fff);
    border-bottom: 1px solid #f1f3f5;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    border-left: 3px solid #16a34a;
}

.station-section-body {
    padding: 1.25rem 1.5rem;
    min-height: 120px;
}

// ─── Tab Header ────────────────────────────────────────────────────────────
.station-tab-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;
    background: #f9fafb;
    border-bottom: 1px solid #e9ecef;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: 540px) {
        padding: 0.75rem 1rem;
        justify-content: center;
    }
}

// ── Underline tab bar (left) ───────────────────────────────────────────────
.station-tab-bar {
    display: flex;
    align-items: flex-end;
    gap: 0;

    @media (max-width: 540px) {
        display: none;
    }
}

.station-tab-btn {
    position: relative;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    outline: none;

    &:focus-visible .tab-btn-inner {
        box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
        border-radius: 8px;
    }
}

.tab-btn-inner {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.9rem 1.1rem 0.85rem;
    font-size: 0.84rem;
    font-weight: 500;
    color: #6b7280;
    transition: color 0.18s;
}

.station-tab-btn.active .tab-btn-inner {
    color: #15803d;
}

.station-tab-btn:not(.active):hover .tab-btn-inner {
    color: #374151;
}

.tab-icon {
    font-size: 0.8rem;
    opacity: 0.75;
}

.station-tab-btn.active .tab-icon {
    opacity: 1;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #f3f4f6;
    color: #9ca3af;
    transition: background 0.18s, color 0.18s;

    &.active {
        background: #dcfce7;
        color: #15803d;
    }
}

.tab-indicator {
    position: absolute;
    bottom: 0;
    left: 1.1rem;
    right: 1.1rem;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: #16a34a;
    animation: slideIn 0.2s ease;
}

@keyframes slideIn {
    from {
        transform: scaleX(0);
        opacity: 0;
    }

    to {
        transform: scaleX(1);
        opacity: 1;
    }
}

// ── Pill toggle (right / mobile fallback) ─────────────────────────────────
.tab-toggle-pill {
    position: relative;
    display: flex;
    align-items: center;
    background: #f3f4f6;
    border-radius: 999px;
    padding: 3px;
    gap: 0;
    border: 1px solid #e5e7eb;
    flex-shrink: 0;
    margin: 0.6rem 0;

    @media (max-width: 540px) {
        margin: 0;
    }
}

.pill-slider {
    position: absolute;
    top: 3px;
    bottom: 3px;
    width: calc(50% - 3px);
    background: #fff;
    border-radius: 999px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    left: 3px;

    &.inventory {
        transform: translateX(100%);
    }
}

.pill-option {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.38rem 0.9rem;
    border: none;
    background: none;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #9ca3af;
    cursor: pointer;
    transition: color 0.18s;
    white-space: nowrap;

    i {
        font-size: 0.72rem;
    }

    &.active {
        color: #15803d;
    }

    &:not(.active):hover {
        color: #374151;
    }
}

// ─── Tab Panels ────────────────────────────────────────────────────────────
.station-tab-panels {
    position: relative;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter-from {
    opacity: 0;
    transform: translateY(4px);
}

.tab-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
